import { useCallback, useMemo, type KeyboardEvent } from 'react';
import { createScanDetector, type ScanDetectorOptions } from '@/lib/barcode-scan';

/**
 * Make a search input scanner-aware. Attach the returned `onKeyDown` (and
 * `onBlur`) to the field; when a hardware scanner finishes a code, `onScan` is
 * called with just that code — the caller sets it as the field's whole value
 * (replacing anything already there) and kicks off the search. Ordinary typing
 * and a hand-typed Enter are untouched.
 *
 * The scan's terminator keystroke is swallowed so it can't submit an enclosing
 * form or move focus with Tab.
 */
export function useBarcodeScan(onScan: (code: string) => void, options?: ScanDetectorOptions) {
  const detector = useMemo(() => createScanDetector(options), [options]);

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLInputElement>) => {
      const code = detector.keydown(e.key, e.timeStamp);
      if (code == null) return;
      e.preventDefault();
      onScan(code);
    },
    [detector, onScan],
  );
  const onBlur = useCallback(() => detector.reset(), [detector]);

  return { onKeyDown, onBlur };
}
